import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { StatCard } from "@/pages/app/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, Euro, Clock, Users, CalendarDays, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
    const { t, lang } = useI18n();
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [salons, setSalons] = useState([]);
    const [config, setConfig] = useState(null);
    const [savingCfg, setSavingCfg] = useState(false);

    const load = () => {
        api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
        api.get("/admin/salons").then(({ data }) => setSalons(data)).catch(() => {});
        api.get("/admin/config").then(({ data }) => setConfig(data)).catch(() => {});
    };
    useEffect(load, []);

    const suspend = async (id, val) => {
        try { await api.post(`/admin/salons/${id}/${val ? "suspend" : "reactivate"}`); load(); toast.success(t("common.saved")); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const saveConfig = async () => {
        setSavingCfg(true);
        try {
            await api.patch("/admin/config", {
                trial_days: Number(config.trial_days),
                plans: config.plans,
                reminder_hours: config.reminder_hours,
            });
            toast.success(t("common.saved"));
        } catch (e) { toast.error(formatApiError(e)); } finally { setSavingCfg(false); }
    };

    const setPlanVal = (key, field, val) => {
        setConfig((c) => ({ ...c, plans: { ...c.plans, [key]: { ...c.plans[key], [field]: val } } }));
    };

    const statusBadge = (s) => {
        const map = { trial: "bg-accent/12 text-accent", active: "bg-green-100 text-green-700", cancelled: "bg-secondary text-muted-foreground", expired: "bg-red-100 text-red-700", past_due: "bg-yellow-100 text-yellow-700" };
        return map[s] || "bg-secondary";
    };

    return (
        <div className="min-h-screen bg-secondary/20">
            <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 px-6 py-4 backdrop-blur-xl">
                <div className="mx-auto flex max-w-7xl items-center justify-between">
                    <div className="flex items-center gap-3"><Logo /><span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">Admin</span></div>
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher compact />
                        <Button variant="ghost" onClick={() => { logout(); nav("/"); }} className="rounded-full" data-testid="admin-logout"><LogOut className="mr-1 h-4 w-4" />{t("common.logout")}</Button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl px-6 py-8">
                <h1 className="mb-6 font-serif text-3xl font-bold">{t("admin.title")}</h1>
                <Tabs defaultValue="overview">
                    <TabsList>
                        <TabsTrigger value="overview" data-testid="admin-tab-overview">{t("admin.overview")}</TabsTrigger>
                        <TabsTrigger value="salons" data-testid="admin-tab-salons">{t("admin.salons")}</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="admin-tab-settings">{t("admin.settings")}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview">
                        {!stats ? <Loader2 className="mx-auto mt-10 h-8 w-8 animate-spin text-accent" /> : (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                <StatCard label={t("admin.totalSalons")} value={stats.total_salons} icon={Building2} />
                                <StatCard label={t("admin.mrr")} value={`€${stats.mrr.toFixed(2)}`} icon={Euro} accent />
                                <StatCard label={t("admin.trialSalons")} value={stats.trial_salons} icon={Clock} />
                                <StatCard label={t("admin.activeSubs")} value={stats.active_basic + stats.active_professional} icon={Users} />
                                <StatCard label="Basic" value={stats.active_basic} />
                                <StatCard label="Professional" value={stats.active_professional} />
                                <StatCard label={t("admin.totalUsers")} value={stats.total_users} icon={Users} />
                                <StatCard label={t("admin.totalAppointments")} value={stats.total_appointments} icon={CalendarDays} />
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="salons">
                        <div className="overflow-hidden rounded-2xl border border-border/70 bg-card">
                            <table className="w-full text-sm">
                                <thead className="border-b border-border/60 text-left text-xs uppercase text-muted-foreground">
                                    <tr><th className="px-4 py-3">Salon</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Plan</th><th className="hidden px-4 py-3 md:table-cell">{t("employees.title")}</th><th className="px-4 py-3">{t("common.actions")}</th></tr>
                                </thead>
                                <tbody>
                                    {salons.map((s) => (
                                        <tr key={s.id} data-testid={`admin-salon-${s.id}`} className="border-b border-border/60 last:border-0">
                                            <td className="px-4 py-3"><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.email}</p></td>
                                            <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge(s.subscription.status)}`}>{s.subscription.status}{s.suspended ? " · suspended" : ""}</span></td>
                                            <td className="px-4 py-3 capitalize">{s.subscription.plan || "—"}</td>
                                            <td className="hidden px-4 py-3 md:table-cell">{s.employee_count}</td>
                                            <td className="px-4 py-3">
                                                <Button variant={s.suspended ? "outline" : "ghost"} size="sm" onClick={() => suspend(s.id, !s.suspended)} className="rounded-full text-xs" data-testid={`admin-suspend-${s.id}`}>
                                                    {s.suspended ? t("admin.reactivate") : t("admin.suspend")}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>

                    <TabsContent value="settings">
                        {!config ? <Loader2 className="mx-auto mt-10 h-8 w-8 animate-spin text-accent" /> : (
                            <div className="max-w-xl space-y-6 rounded-2xl border border-border/70 bg-card p-6">
                                <div><Label>{t("admin.trialDays")}</Label><Input type="number" value={config.trial_days} onChange={(e) => setConfig({ ...config, trial_days: e.target.value })} data-testid="cfg-trial-days" className="mt-1.5 max-w-[160px]" /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label>{t("admin.basicPrice")} (€)</Label><Input type="number" value={config.plans.basic.price} onChange={(e) => setPlanVal("basic", "price", Number(e.target.value))} data-testid="cfg-basic-price" className="mt-1.5" /></div>
                                    <div><Label>{t("admin.basicLimit")}</Label><Input type="number" value={config.plans.basic.employee_limit} onChange={(e) => setPlanVal("basic", "employee_limit", Number(e.target.value))} data-testid="cfg-basic-limit" className="mt-1.5" /></div>
                                </div>
                                <div><Label>{t("admin.proPrice")} (€)</Label><Input type="number" value={config.plans.professional.price} onChange={(e) => setPlanVal("professional", "price", Number(e.target.value))} data-testid="cfg-pro-price" className="mt-1.5 max-w-[160px]" /></div>
                                <p className="text-xs text-muted-foreground">Professional employee limit is 0 (unlimited).</p>
                                <Button onClick={saveConfig} disabled={savingCfg} className="rounded-full" data-testid="save-config">{savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
